import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";

/**
 * RichesseStrip — Showcase the site's richness immediately after the hero,
 * before any heavy editorial reading. Numbers + 4 quick discovery shortcuts.
 */
export default function RichesseStrip() {
    const [stats, setStats] = useState({
        places: null, walks: null, experiences: null, events: null, articles: null,
    });

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/places", { params: { limit: 1, include_hidden: false } }).then(r => r.headers["x-total-count"] || null).catch(() => null),
            api.get("/places", { params: { limit: 500, include_hidden: false } }).then(r => Array.isArray(r.data) ? r.data.length : null).catch(() => null),
            api.get("/walks", { params: { limit: 100 } }).then(r => Array.isArray(r.data) ? r.data.length : null).catch(() => null),
            api.get("/experiences", { params: { limit: 100 } }).then(r => Array.isArray(r.data) ? r.data.length : null).catch(() => null),
            api.get("/events-live", { params: { limit: 1 } }).then(r => r.data?.total_available || r.data?.count || null).catch(() => null),
            api.get("/articles", { params: { limit: 100 } }).then(r => Array.isArray(r.data) ? r.data.length : null).catch(() => null),
        ]).then(([_, places, walks, xps, events, articles]) => {
            if (!alive) return;
            setStats({ places, walks, experiences: xps, events, articles });
        });
        return () => { alive = false; };
    }, []);

    const NUMBERS = [
        { value: stats.places, suffix: "", label: "Adresses vérifiées", to: "/adresses" },
        { value: stats.experiences, suffix: "", label: "Expériences", to: "/experiences" },
        { value: stats.walks, suffix: "", label: "Balades GPS", to: "/balades" },
        { value: stats.events, suffix: stats.events && stats.events > 100 ? "+" : "", label: "Évènements live", to: "/evenements" },
    ];

    const SHORTCUTS = [
        {
            kicker: "Chapitre I",
            title: "Le territoire",
            desc: "16 communes, du Tréport à Aumale.",
            to: "/destinations",
            testid: "richesse-shortcut-destinations",
        },
        {
            kicker: "Chapitre II",
            title: "Les adresses",
            desc: "Hôtels, tables, antiquaires — notés Google ≥ 4,5/5.",
            to: "/adresses",
            testid: "richesse-shortcut-adresses",
        },
        {
            kicker: "Chapitre IV",
            title: "Les balades",
            desc: "Forêt royale d'Eu, falaises, Bresle.",
            to: "/balades",
            testid: "richesse-shortcut-balades",
        },
        {
            kicker: "Cartographie",
            title: "La carte",
            desc: "Tout le territoire au millimètre.",
            to: "/carte",
            testid: "richesse-shortcut-carte",
        },
    ];

    return (
        <section
            data-testid="richesse-strip"
            className="bg-cp-ivoire-bright border-y border-cp-border py-14 md:py-20"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                {/* === Numbers row === */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 md:gap-x-12 mb-14 md:mb-20 border-b border-cp-border pb-12 md:pb-16">
                    {NUMBERS.map((n, i) => (
                        <Link
                            key={i}
                            to={n.to}
                            data-testid={`richesse-stat-${i}`}
                            className="group block"
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                            >
                                <p className="cp-serif text-6xl md:text-7xl lg:text-8xl text-cp-encre leading-[0.9] tracking-tight group-hover:text-cp-foret transition-colors">
                                    {n.value !== null ? n.value : <span className="opacity-30">·</span>}
                                    {n.suffix && <span className="cp-display-italic text-cp-sable-deep">{n.suffix}</span>}
                                </p>
                                <p className="cp-tiny text-cp-gris mt-3 group-hover:text-cp-foret transition-colors">
                                    {n.label}
                                </p>
                            </motion.div>
                        </Link>
                    ))}
                </div>

                {/* === Header === */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
                    <div className="md:col-span-5">
                        <p className="cp-tiny text-cp-foret mb-4">Au sommaire</p>
                        <h2 className="cp-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-cp-encre">
                            Par où <em className="cp-display-italic">commencer.</em>
                        </h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 cp-serif text-lg md:text-xl italic font-light text-cp-gris leading-relaxed self-end">
                        Quatre entrées, autant de lectures du même territoire — choisissez la vôtre.
                    </p>
                </div>

                {/* === 4 Shortcut tiles === */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cp-border">
                    {SHORTCUTS.map((s, i) => (
                        <Link
                            key={i}
                            to={s.to}
                            data-testid={s.testid}
                            className="group bg-cp-ivoire-bright p-8 md:p-10 hover:bg-cp-craie transition-colors block min-h-[200px] flex flex-col justify-between"
                        >
                            <p className="cp-tiny text-cp-sable-deep mb-3">{s.kicker}</p>
                            <div>
                                <h3 className="cp-serif text-3xl md:text-4xl text-cp-encre leading-tight mb-3 group-hover:italic transition-all">
                                    {s.title}
                                </h3>
                                <p className="cp-serif italic text-base font-light text-cp-gris leading-snug mb-6">
                                    {s.desc}
                                </p>
                                <span className="cp-tiny text-cp-foret opacity-60 group-hover:opacity-100 transition-opacity">
                                    Entrer →
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
